import { AppComponent } from './app.component';
import { AppLayoutComponent } from '@layout/app-layout.component';
import {
  ArticleResolver,
  AuthorResolver,
  BrowserStateInterceptor,
  CategoryResolver,
  SearchTagsResolver,
  SlugGuard
} from '@core/services';
import {
  BreadcrumbComponent,
  CarouselComponent,
  CategoryMenuComponent,
  CategoryMenuSectionComponent,
  CategorySectionComponent,
  FooterComponent,
  FooterUsComponent,
  HeroStepperComponent,
  NewsletterDialogComponent,
  NewsletterFormComponent,
  PaginationComponent,
  SearchComponent,
  SideMenuComponent,
  SnippetHeroComponent,
  SnippetMobileComponent,
  StickyBannerComponent,
  TrialComponent,
  TrialUsComponent
} from '@shared/sections';
import { BrowserJsonLdModule } from '@core/seo/json-ld/browser-json-ld.module';
import { BrowserModule } from '@angular/platform-browser';
import { DatePipe } from '@angular/common';
import { GhSafeHtmlPipe } from '@shared/pipes/safe-html.pipe';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { MaterialModule } from '@core/material.module';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { ServicesModule } from '@core/services.module';
import {
  ArticleComponent,
  AuthorComponent,
  CategoryComponent,
  CategoryLayoutComponent,
  HomeComponent,
  NotFoundComponent,
  SearchResultsComponent,
} from '@features';
import { OneTrustInitService } from '@core/services/one-trust-init.service';
import { LogoComponent } from '@shared/svgs';

const routes: Routes = [
  {
    path: '404', component: AppLayoutComponent,
    children: [
      { path: '', component: NotFoundComponent },
    ]
  },
  {
    path: '', component: AppLayoutComponent,
    children: [
      { path: '', component: HomeComponent },
      { path: 'page/:page', component: HomeComponent },
      {
        path: 'search-results', component: CategoryLayoutComponent,
        children: [
          { path: '', component: SearchResultsComponent, resolve: { searchResults: SearchTagsResolver } },
          { path: 'terms/:terms', component: SearchResultsComponent, resolve: { searchResults: SearchTagsResolver } },
          { path: 'terms/:terms/page/:page', component: SearchResultsComponent, resolve: { searchResults: SearchTagsResolver } },
        ]
      },
      { path: 'author/:slug', component: AuthorComponent, resolve: { author: AuthorResolver } },
      { path: 'author/:slug/page/:page', component: AuthorComponent, resolve: { author: AuthorResolver } },
      {
        path: ':category', component: CategoryLayoutComponent,
        children: [
          { path: '', component: CategoryComponent, resolve: { category: CategoryResolver } },
          { path: ':article', component: ArticleComponent, resolve: { article: ArticleResolver } },
        ]
      },
      { path: ':category/page/:page', component: CategoryComponent, resolve: { category: CategoryResolver } },
    ]
  },
  {
    path: '**', redirectTo: '/404',
  }
];

const pages = [
  ArticleComponent,
  AppComponent,
  AppLayoutComponent,
  AuthorComponent,
  CategoryComponent,
  CategoryLayoutComponent,
  HomeComponent,
  NotFoundComponent,
  SearchResultsComponent
];
const sections = [
  BreadcrumbComponent,
  CarouselComponent,
  CategoryMenuComponent,
  CategoryMenuSectionComponent,
  CategorySectionComponent,
  FooterComponent,
  FooterUsComponent,
  HeroStepperComponent,
  NewsletterFormComponent,
  PaginationComponent,
  SideMenuComponent,
  SnippetHeroComponent,
  SnippetMobileComponent,
  SearchComponent,
  TrialComponent,
  TrialUsComponent
];
const dialogs = [
  NewsletterDialogComponent,
  StickyBannerComponent
];
const pipes = [
  GhSafeHtmlPipe
];
const providers = [
  { provide: HTTP_INTERCEPTORS, useClass: BrowserStateInterceptor, multi: true },
  DatePipe,
  // guards only
  SlugGuard,
  // resolvers
  ArticleResolver,
  AuthorResolver,
  CategoryResolver,
  SearchTagsResolver,
  {
    provide: APP_INITIALIZER,
    useFactory: initOneTrust,
    deps: [OneTrustInitService],
    multi: true,
  },
];
const imports = [
  BrowserModule,
  BrowserJsonLdModule,
  MaterialModule,
  RouterModule.forRoot(routes, {
    initialNavigation: 'enabledBlocking',
    scrollPositionRestoration: 'top'
}),
  ServicesModule.forRoot(),
];

const svgs = [
  LogoComponent
]

@NgModule({
    bootstrap: [AppComponent],
    declarations: [...pages, ...sections, ...dialogs, ...pipes, ...svgs],
    exports: [...pages, ...sections, ...dialogs, ...pipes, ...svgs],
    imports,
    providers
})
export class AppModule { }

export function initOneTrust(oneTrust: OneTrustInitService): () => void | Promise<unknown> {
  return () => oneTrust.load();
}
